import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
// APX-522: both notification-list cases below delegate to the one shared transform,
// `buildNotificationsListState`, which owns grouping and keeps the server's order.
// `isAscendingSort` only records the list's direction from the page-1 request.
import { buildNotificationsListState, isAscendingSort } from 'src/helper/notification-utils';
import {
  getClearedNotification,
  getNotification,
  getNotificationsCount,
  getProfile,
  readAllNotification,
  signUp,
  syncUser,
  updateTemplate,
} from 'src/redux/profile/api';
import type {
  InitialProfileStateType,
  NotificationType,
  ServiceRequestEntity,
  WorkOrderEntity,
} from 'src/redux/profile/types';

type EntityMaps = {
  workOrderEntities: Record<string, WorkOrderEntity>;
  serviceRequestEntities: Record<string, ServiceRequestEntity>;
};

const extractEntitiesFromNotifications = (
  notifications: NotificationType[],
  existing: EntityMaps,
): EntityMaps => {
  const workOrderEntities = { ...existing.workOrderEntities };
  const serviceRequestEntities = { ...existing.serviceRequestEntities };

  notifications.forEach((item) => {
    if (item?.work_order?.work_order_id) {
      workOrderEntities[item?.work_order?.work_order_id] = item?.work_order;
    }
    if (item?.service_request?.service_request_id) {
      serviceRequestEntities[item?.service_request?.service_request_id] = item?.service_request;
    }
  });

  return { workOrderEntities, serviceRequestEntities };
};

const extraReducers = (builder: ActionReducerMapBuilder<InitialProfileStateType>) => {
  /**
   * user details
   */

  builder.addCase(getProfile().pending, (state: InitialProfileStateType) => {
    state.app.userDetails.isLoading = true;
  });
  builder.addCase(
    getProfile().fulfilled,
    (
      state: InitialProfileStateType,
      action: PayloadAction<InitialProfileStateType['app']['userDetails']['data']>,
    ) => {
      state.app.userDetails.isLoading = false;
      state.app.userDetails.data = action?.payload;
      if (action?.payload?.markets) {
        state.markets = action.payload.markets.map((market: { id: string; name: string }) => ({
          label: market.name,
          value: market.id,
        }));
      }
    },
  );
  builder.addCase(getProfile().rejected, (state: InitialProfileStateType) => {
    state.app.userDetails.isLoading = false;
  });

  /**
   * Sync Profile
   */
  builder.addCase(syncUser().pending, (state: InitialProfileStateType) => {
    state.app.userDetails.isLoading = true;
  });
  builder.addCase(syncUser().fulfilled, (state: InitialProfileStateType) => {
    state.app.userDetails.isLoading = false;
  });
  builder.addCase(syncUser().rejected, (state: InitialProfileStateType) => {
    state.app.userDetails.isLoading = false;
  });

  /**
   * sign up
   */

  builder.addCase(signUp().pending, (state: InitialProfileStateType) => {
    state.signUp.isLoading = true;
  });
  builder.addCase(signUp().fulfilled, (state: InitialProfileStateType) => {
    state.signUp.isLoading = false;
  });
  builder.addCase(signUp().rejected, (state: InitialProfileStateType) => {
    state.signUp.isLoading = false;
  });

  builder.addCase(updateTemplate().pending, (state: InitialProfileStateType) => {
    state.isThemeLoading = true;
  });
  builder.addCase(updateTemplate().fulfilled, (state: InitialProfileStateType) => {
    state.isThemeLoading = false;
  });
  builder.addCase(updateTemplate().rejected, (state: InitialProfileStateType) => {
    state.isThemeLoading = false;
  });

  // builder.addCase(getSubMenusCount().pending, (state: InitialProfileStateType) => {
  //   state.app.fetchingSubMenusDetails = true;
  // });
  // builder.addCase(getSubMenusCount().fulfilled, (
  //   state: InitialProfileStateType,
  //   action: PayloadAction<Array<{ view_uuid: string, count: number }>>,
  // ) => {
  //   const { payload } = action;
  //   state.app.fetchingSubMenusDetails = false;
  //   payload?.forEach((item) => {
  //     state.app.subMenusCountDetails[item?.view_uuid] = item?.count;
  //   });
  // });
  // builder.addCase(getSubMenusCount().rejected, (state: InitialProfileStateType) => {
  //   state.app.fetchingSubMenusDetails = false;
  // });

  // notification
  builder.addCase(getNotification().pending, (state: InitialProfileStateType) => {
    state.isAllNotificationFetching = true;
  });

  builder.addCase(getNotification().fulfilled, (state: InitialProfileStateType, action) => {
    // Preserve existing notifications when paginating
    const isPaginating = !!action?.payload?.meta?.prev;

    // APX-522: the list transform is SHARED with the server-rendered
    // /work-queues page (src/server/work-queue-rows.ts) so both paths produce
    // the same ids, grouping and order by construction. Paginate-vs-fresh is
    // expressed by which `existing` pair we pass. Called synchronously inside
    // the producer — see the Immer contract on buildNotificationsListState.
    const { ids: sortedIds, source: newSource } = buildNotificationsListState(
      action?.payload?.data || [],
      isPaginating
        ? { ids: state?.notifications?.data || [], source: state.notificationSource || {} }
        : { ids: [], source: {} },
    );

    // Extract and store full WO/SR entities
    const existing = isPaginating
      ? {
          workOrderEntities: state.workOrderEntities,
          serviceRequestEntities: state.serviceRequestEntities,
        }
      : { workOrderEntities: {}, serviceRequestEntities: {} };
    const { workOrderEntities, serviceRequestEntities } = extractEntitiesFromNotifications(
      action?.payload?.data || [],
      existing,
    );
    state.workOrderEntities = workOrderEntities;
    state.serviceRequestEntities = serviceRequestEntities;

    state.notifications = {
      data: sortedIds,
      meta: action?.payload?.meta || {},
      // A page 1 sets the list's direction; later cursor pages continue it.
      ascending: isPaginating
        ? state.notifications?.ascending
        : isAscendingSort(action?.meta?.arg?.sort),
    };
    state.notificationSource = newSource;
    state.isAllNotificationFetching = false;
  });
  builder.addCase(getNotification().rejected, (state: InitialProfileStateType) => {
    state.isAllNotificationFetching = false;
  });

  // server-authoritative unread notifications count
  builder.addCase(
    getNotificationsCount().fulfilled,
    (state: InitialProfileStateType, action: PayloadAction<{ count?: number } | undefined>) => {
      const count = action?.payload?.count;
      if (typeof count === 'number') {
        state.app.userDetails.data.un_read_notification_count = Math.max(0, count);
      }
    },
  );

  builder.addCase(readAllNotification().pending, (state: InitialProfileStateType) => {
    state.isMarkAllReadLoading = true;
  });

  builder.addCase(readAllNotification().fulfilled, (state: InitialProfileStateType) => {
    state.isMarkAllReadLoading = false;
  });

  builder.addCase(readAllNotification().rejected, (state: InitialProfileStateType) => {
    state.isMarkAllReadLoading = false;
  });

  builder.addCase(getClearedNotification().pending, (state: InitialProfileStateType) => {
    state.isClearedNotificationFetching = true;
  });

  builder.addCase(getClearedNotification().fulfilled, (state: InitialProfileStateType, action) => {
    // Preserve existing cleared notifications when paginating
    const isPaginating = !!action?.payload?.meta?.prev;

    // APX-522: the SAME shared transform as the active-notifications case above.
    // This is the other half of Decision 3 — until it was routed through here, the
    // cleared tab carried a byte-identical copy of the whole block and could
    // silently diverge from the shared implementation.
    const { ids: sortedIds, source: newSource } = buildNotificationsListState(
      action?.payload?.data || [],
      isPaginating
        ? {
            ids: state?.clearedNotifications?.data || [],
            source: state.clearedNotificationSource || {},
          }
        : { ids: [], source: {} },
    );

    // Extract and store full WO/SR entities (always merge to preserve active-tab entities)
    const { workOrderEntities, serviceRequestEntities } = extractEntitiesFromNotifications(
      action?.payload?.data || [],
      {
        workOrderEntities: state.workOrderEntities,
        serviceRequestEntities: state.serviceRequestEntities,
      },
    );
    state.workOrderEntities = workOrderEntities;
    state.serviceRequestEntities = serviceRequestEntities;

    state.clearedNotifications = {
      data: sortedIds,
      meta: action?.payload?.meta || {},
      ascending: isPaginating
        ? state.clearedNotifications?.ascending
        : isAscendingSort(action?.meta?.arg?.sort),
    };
    state.clearedNotificationSource = newSource;
    state.isClearedNotificationFetching = false;
  });

  builder.addCase(getClearedNotification().rejected, (state: InitialProfileStateType) => {
    state.isClearedNotificationFetching = false;
  });
};

export default extraReducers;
